import { useEffect, useRef, useState, type CSSProperties } from "react";
import { client } from "../net/client";
import { useSignal } from "../net/signal";
import { fa, faNum } from "../game/cards";
import { avatarSrc } from "../game/avatar";
import { gameArt, gameTint, games } from "./games";
import type { PublicTable } from "../gen/bazivan/v1/table_pb";
import { BotIcon } from "./BotIcon";
import { botBare } from "../game/seats";
import { placeLabel } from "../game/place";
import { playAfterAd } from "./Ads";
import { CountryFlag } from "./CountryFlag";

/**
 * فهرستِ میزهای باز — «ببین بقیه چطور بازی می‌کنند».
 *
 * هر میزی که سرور در فهرست بدهد این‌جا می‌آید — میزِ پر از ربات هم (صفحه‌ی
 * اول آن را در شمارِ «زنده» می‌شمارد، پس این‌جا هم باید دیده شود). صندلیِ ربات
 * با نشانِ ربات و چهره‌ی کم‌رنگ از آدم جدا می‌شود. تماشاچی دستِ هیچ‌کس را نمی‌بیند — نه دستِ خودش
 * (ندارد) و نه دستِ بازیکن‌ها — وگرنه یک نفر کنارِ رفیقش می‌نشست و بازی را
 * برایش می‌برد.
 *
 * چیدمان لابی است نه فهرست: هر میز یک کارت با آرت و نامِ بازی، چهره‌های درشت و
 * یک «تماشا» ی جمع‌وجور کنارِ نام؛ کلِ کارت دکمه است. روی دسکتاپ سه ستون، روی گوشی یکی.
 */
const REFRESH_MS = 5000;

/**
 * بلندترین فهرستی که سرور می‌دهد (Backend/internal/ws/watch.go → maxListed). تا زیرِ
 * این است همه‌ی میزها یک‌جا می‌آیند و صافیِ بازی همین‌جا اعمال می‌شود — پس شمارِ هر
 * بازی روی دکمه‌اش می‌نشیند و عوض‌کردنِ صافی بی‌درنگ است. فهرستِ به سقف‌رسیده بُریده
 * است (پرتماشاچی‌ها اول)، و میزِ کم‌تماشاچیِ یک بازی در آن گم می‌شد؛ از آن به بعد صافی
 * دوباره به سرور می‌رود و شمارها پنهان می‌شوند — شمارِ بُریده دروغ می‌گفت.
 */
const MAX_LISTED = 50;

/** تا این‌قدر بی پاسخ، اسکلت جایش را به «میزی نیست» می‌دهد — اسکلتِ ابدی بدتر از هر دو است. */
const LOADING_MS = 6000;

export function Watch({ onBack }: { onBack: () => void }) {
  const tables = useSignal(client.tables);
  const [game, setGame] = useState("");
  const [narrow, setNarrow] = useState(false);
  useEffect(() => { if (tables.length >= MAX_LISTED) setNarrow(true); }, [tables]);

  // فهرستِ کهنه از دیدنِ قبلی هنوز در client.tables است. پاسخِ تازه همیشه آرایه‌ی تازه است،
  // پس «هنوز چیزی نرسیده» یعنی همان آرایه‌ی خالیِ لحظه‌ی باز شدن.
  const first = useRef(tables);
  const [late, setLate] = useState(false);
  useEffect(() => { const t = setTimeout(() => setLate(true), LOADING_MS); return () => clearTimeout(t); }, []);
  const loading = tables === first.current && tables.length === 0 && !late;

  // فهرست را سرور خودش نمی‌فرستد: تا این صفحه باز است می‌پرسیم، و همین که
  // بسته شد پرسیدن تمام می‌شود.
  //
  // برگه‌ی پشت هم نمی‌پرسد: فهرستی که کسی نمی‌بیند هر پنج ثانیه بسته‌ی
  // اینترنت و باتری می‌خورَد — و بازار موبایل است. در عوض همین که برگه
  // برگردد یک‌بار می‌پرسیم، وگرنه تا پنج ثانیه فهرستِ کهنه می‌دیدی.
  // فهرستِ تماشا سرور را می‌خواهد: مرورگرِ بی‌حساب همین‌جا مهمانش را می‌گیرد (و نه زودتر).
  // با state و نه یک‌بار سرِ ساخت: وقتی صفحه باز می‌شود پرسشِ «این مرورگر حسابی دارد؟» شاید
  // هنوز در راه باشد (loggingIn) و signedOut کمی بعد می‌رسد — بی این، فهرست هرگز پرسیده
  // نمی‌شد و صفحه به دروغ «میزی برای تماشا نیست» می‌گفت.
  const conn = useSignal(client.state);
  useEffect(() => {
    if (conn === "signedOut") void client.connect({ create: true });
  }, [conn]);
  const ask = narrow ? game : "";
  useEffect(() => {
    const poll = () => { if (document.visibilityState === "visible") client.listTables(ask); };
    poll();
    const t = setInterval(poll, REFRESH_MS);
    document.addEventListener("visibilitychange", poll);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", poll); };
    // conn: همین که وصل شد یک‌بار بی‌درنگ بپرس، نه پنج ثانیه بعد.
  }, [ask, conn]);

  const shown = game ? tables.filter((t) => t.game === game) : tables;
  const count = new Map<string, number>();
  for (const t of tables) count.set(t.game, (count.get(t.game) ?? 0) + 1);
  const watchers = shown.reduce((n, t) => n + t.watchers, 0);
  const picked = games.find((g) => g.id === game);
  // روی گوشی صافی‌ها یک ردیفِ لغزان‌اند: دکمه‌ای که نیمه‌اش بیرونِ لبه بود، با زدن کامل به دید می‌آید.
  const pick = (el: HTMLElement, id: string) => {
    setGame(id);
    el.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
  };

  return (
    <div className="page stack watchpage">
      <header className="wt-head">
        <h2>میزهای زنده</h2>
        {!loading && shown.length > 0 && (
          <span className="wt-sum">
            <i className="wt-live" aria-hidden="true" />
            {fa(shown.length)} میز{watchers > 0 && ` · ${fa(watchers)} تماشاچی`}
          </span>
        )}
        <p className="muted">تماشا رایگان است؛ دست بازیکن‌ها پنهان می‌ماند.</p>
      </header>

      <div className="wt-filters" role="group" aria-label="بازی">
        <button className={"chip" + (game === "" ? " on" : "")} aria-pressed={game === ""} onClick={(e) => pick(e.currentTarget, "")}>
          همه{!narrow && !loading && <span className="wt-n">{fa(tables.length)}</span>}
        </button>
        {games.filter((g) => g.online).map((g) => {
          const n = count.get(g.id) ?? 0;
          return (
            <button key={g.id} className={"chip" + (game === g.id ? " on" : "") + (!narrow && !loading && n === 0 ? " none" : "")}
                    aria-pressed={game === g.id} onClick={(e) => pick(e.currentTarget, g.id)}>
              <img src={gameArt(g.id)} alt="" width={22} height={22} decoding="async" />
              {g.fa}
              {!narrow && n > 0 && <span className="wt-n">{fa(n)}</span>}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="wt-grid" role="status" aria-busy="true" aria-label="در حال گرفتن فهرست میزها…">
          {[0, 1, 2].map((i) => <div key={i} className="wt-card skel" aria-hidden="true"><i /><i /><i /></div>)}
        </div>
      ) : shown.length === 0 ? (
        <div className="plate wt-empty">
          {picked
            ? <span className="wt-art big" style={{ "--c": gameTint(picked.color) } as CSSProperties}><img src={gameArt(picked.id)} alt="" width={64} height={64} /></span>
            : <EyeIcon size={40} />}
          <b>{picked ? `الان میز ${picked.fa} برای تماشا نیست.` : "الان میز بازی برای تماشا نیست."}</b>
          <div className="muted">
            میزهای زنده، چه با آدم چه با ربات، همین‌جا می‌آیند. اتاق خصوصی دوستان پخش نمی‌شود.
          </div>
          {picked && (narrow || tables.length > 0) && (
            <button className="btn small" onClick={() => setGame("")}>
              همه‌ی میزها{!narrow && ` (${fa(tables.length)})`}
            </button>
          )}
        </div>
      ) : (
        <div className="wt-grid">
          {shown.map((t) => <TableCard key={t.tableId} t={t} />)}
        </div>
      )}

      <button className="btn ghost wt-back" onClick={onBack}>برگرد</button>
    </div>
  );
}

function TableCard({ t }: { t: PublicTable }) {
  const g = games.find((x) => x.id === t.game);
  const title = g?.fa ?? t.game;
  // ربات: آیکون کنارِ نام و نه واژه‌ی «ربات» — [[botBare]]. ربات بی‌نام فقط آیکون می‌ماند. برچسبِ
  // صفحه‌خوان (پایین) همان نامِ کاملِ «ربات کاوه» را می‌گیرد: آیکون را نمی‌بیند و شکلک را انگلیسی می‌خوانَد.
  const names = t.seats.map((s) => (s.bot ? botBare(s.name) : s.name || "بازیکن"));
  return (
    // تماشاچی پیش از هر میز کارتِ آگهی می‌بیند، بی سقف‌های آهنگ — [[playAfterAd]]، [[beforeTable]].
    <button className="wt-card" style={g ? { "--c": gameTint(g.color) } as CSSProperties : undefined}
            aria-label={`تماشای ${title}: ${t.seats.map((s, i) => (s.bot ? s.name || "ربات" : names[i])).join("، ")}`}
            onClick={() => playAfterAd(() => client.watchTable(t.tableId), "watch")}>
      <span className="wt-top">
        <span className="wt-art">{g && <img src={gameArt(g.id)} alt="" width={44} height={44} loading="lazy" decoding="async" />}</span>
        <span className="wt-title">
          <b>{title}</b>
          <span className="wt-meta">
            <span><i className="wt-live" aria-hidden="true" />زنده · {sinceText(t.startedAtMs)}</span>
            {t.watchers > 0 && <span>· {fa(t.watchers)} تماشاچی</span>}
          </span>
        </span>
        <span className="wt-go"><EyeIcon size={17} />تماشا</span>
      </span>

      <span className="wt-seats">
        {t.seats.map((s, i) => {
          const place = placeLabel(s);
          return (
            <span key={s.index} className={"wt-seat" + (s.bot ? " bot" : "")}>
              <span className="cflag-host"><img className="seatface" src={avatarSrc(s.avatar)} alt="" width={40} height={40} loading="lazy" decoding="async" /><CountryFlag code={s.country} /></span>
              <span className="wt-who">
                <span className="wt-name" title={names[i]}>{s.bot && <BotIcon size={13} />}{names[i]}</span>
                {place && <span className="wt-place">{place}</span>}
              </span>
            </span>
          );
        })}
      </span>

      {t.stake > 0n && <span className="wt-stake">ورودی میز <img src="/art/ui/ic-coin.webp" alt="" width={16} height={16} /><b>{faNum(t.stake)}</b> سکه</span>}
    </button>
  );
}

function EyeIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor"
         strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/** «۴ دقیقه پیش» — سنِ میز، از ساعتِ سرور، با قالبِ محلیِ مرورگر (Intl، نه دست‌ساز). */
const since = new Intl.RelativeTimeFormat("fa", { numeric: "auto" });
function sinceText(startedAtMs: bigint): string {
  const sec = Math.max(0, Math.round((Date.now() - Number(startedAtMs)) / 1000));
  if (sec < 60) return "تازه شروع شده";
  const min = Math.round(sec / 60);
  if (min < 60) return since.format(-min, "minute");
  return since.format(-Math.round(min / 60), "hour");
}
